import { useCallback, useRef } from "react";

let globalTimerID = 0;
export const useTimer = (onComplete = () => {}, animDuration = Number.POSITIVE_INFINITY) => {
  globalTimerID++;
  const timerID = useRef(globalTimerID);
  const shouldAnimate = useRef(false);
  const frame = useRef(null);
  const startTime = useRef(performance.now());
  const deltaTime = useRef(0);
  const tick = useRef(() => {});

  const stop = useCallback(
    () => {
      if(shouldAnimate.current) {
        shouldAnimate.current = false;
        cancelAnimationFrame(frame.current);
        onComplete();
      }
    },
    [onComplete]
  );

  const loop = useCallback(
    (currentTime) => {
      if(shouldAnimate.current) {
        // Gets the progress percent 0-1
        deltaTime.current = currentTime - startTime.current;
        let animProgress = deltaTime.current / animDuration;
        if(animProgress >= 1) {
          // Stop when the animation is complete
          return stop();
        }
        if(animProgress <= 1) {
          // Still progressing... tick!
          tick.current(animProgress);
          if(animProgress !== 1) {
            // Still progressing, request next frame.
            frame.current = requestAnimationFrame(loop);
          }
        }
      }
    },
    [animDuration, stop]
  );

  const start = useCallback(

    /**
     * start
     * @param {onTickCallback} onTick 
     *        The callback executed during animation update
     */
    (onTick = () => {}) => {
      tick.current = onTick;
      shouldAnimate.current = true;
      frame.current = requestAnimationFrame(loop);
    }, 
    [loop]
  );

  /**
   * enqueue the loop func
   * @returns {number} raf id
   */
  const enqueue = () => {
    startTime.current = performance.now() - deltaTime.current;
    shouldAnimate.current = true;
    return frame.current = requestAnimationFrame(loop);
  };

  /**
   * cancel the animation
   * @return {boolean} is running
   */
  const cancel = () => {
    cancelAnimationFrame(frame.current);
    return shouldAnimate.current = false;
  };

  return ({
    start,
    stop,
    cancel,
    enqueue,
    get running() { return shouldAnimate.current; },
    get id() { return timerID.current; }
  });
};
